import React, { useMemo, useState } from "react";

export type SortDirection = "asc" | "desc";

export type SortState<K extends string> = {
  key: K | null;
  direction: SortDirection;
};

export function useSortedRows<T, K extends string = string>(
  rows: T[],
  getValue: (row: T, key: K) => string | number | null,
  initial?: SortState<K>
) {
  const [sort, setSort] = useState<SortState<K>>(
    initial ?? { key: null, direction: "asc" }
  );

  function toggleSort(key: K) {
    setSort((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  }

  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const k = sort.key;
    const dir = sort.direction === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = getValue(a, k);
      const bv = getValue(b, k);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sort.key, sort.direction, getValue]);

  return { sorted, sort, toggleSort };
}

export function SortCaret({
  activeKey,
  columnKey,
  direction,
}: {
  activeKey: string | null;
  columnKey: string;
  direction: SortDirection;
}) {
  const isActive = activeKey === columnKey;
  if (!isActive) return <span className="oht-sort-caret oht-sort-caret--idle">⇅</span>;
  return (
    <span className="oht-sort-caret oht-sort-caret--active">
      {direction === "asc" ? "▲" : "▼"}
    </span>
  );
}
